import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AdminLevelDefinition, AdminTicketTypeDefinition } from '@central/contracts';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonRegion, SkeletonText } from '@/components/Skeleton';
import {
  alterarImpacto,
  alterarPrioridade,
  alterarTipoDeChamado,
  alterarUrgencia,
  chavesDeAdministracao,
  listarImpactos,
  listarPrioridades,
  listarTiposDeChamado,
  listarUrgencias,
  type AlteracaoDeDefinicao,
} from '@/features/admin/admin.api';
import {
  Cabecalho,
  Celula,
  EtiquetaDeAtividade,
  Linha,
  LinhaDeCabecalho,
  CLASSE_DE_CAMPO,
  useEscritaDeCatalogo,
} from '@/features/admin/componentes';
import { Modal } from '@/features/tickets/Modal';

/**
 * Campos configuráveis de chamado.
 *
 * Prioridade, urgência, impacto e tipo têm um valor de enum fixo — é o que
 * o motor de SLA e as regras leem. O que muda aqui é apenas o rótulo que o
 * operador vê na tela. Não há criação nem remoção: os valores existem enquanto
 * o enum existir.
 */

// ── seção de nível (prioridade / urgência / impacto) ──────────────────────

function SecaoDeNivel({
  titulo,
  resumo,
  chaveDeConsulta,
  queryFn,
  alterarFn,
}: {
  titulo: string;
  resumo: string;
  chaveDeConsulta: readonly unknown[];
  queryFn: () => Promise<{ items: AdminLevelDefinition[] }>;
  alterarFn: (id: string, corpo: AlteracaoDeDefinicao) => Promise<AdminLevelDefinition>;
}) {
  const consulta = useQuery({ queryKey: chaveDeConsulta, queryFn });
  const itens = consulta.data?.items ?? [];
  const [editando, setEditando] = useState<AdminLevelDefinition | null>(null);

  const atividade = useEscritaDeCatalogo(
    ({ id, active, version }: { id: string; active: boolean; version: number }) =>
      alterarFn(id, { active, version }),
  );

  return (
    <section className="space-y-3">
      <div className="space-y-0.5">
        <h3 className="text-base font-semibold">{titulo}</h3>
        <p className="text-sm text-muted-foreground">{resumo}</p>
      </div>

      <Card>
        <CardBody className="p-0">
          {consulta.isError ? (
            <div className="px-5 py-6">
              <ErrorState
                error={consulta.error}
                title="Não foi possível carregar"
                onRetry={() => void consulta.refetch()}
              />
            </div>
          ) : consulta.isPending ? (
            <SkeletonRegion label={`Carregando ${titulo}`} className="space-y-3 px-5 py-5">
              <SkeletonText lines={4} />
            </SkeletonRegion>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <LinhaDeCabecalho>
                  <Cabecalho>Nível</Cabecalho>
                  <Cabecalho>Rótulo</Cabecalho>
                  <Cabecalho>Cor</Cabecalho>
                  <Cabecalho>Ordem</Cabecalho>
                  <Cabecalho>Em uso</Cabecalho>
                  <Cabecalho>Estado</Cabecalho>
                  <Cabecalho className="text-right">Ações</Cabecalho>
                </LinhaDeCabecalho>
                <tbody>
                  {itens.map((item) => (
                    <Linha key={item.id} inativo={!item.active}>
                      <Celula>
                        <code className="font-mono text-xs text-muted-foreground">{item.level}</code>
                      </Celula>
                      <Celula className="font-medium">{item.label}</Celula>
                      <Celula>
                        {item.color ? (
                          <span className="inline-flex items-center gap-2">
                            <span
                              aria-hidden
                              className="h-3.5 w-3.5 rounded-full ring-1 ring-inset ring-border"
                              style={{ backgroundColor: item.color }}
                            />
                            <code className="font-mono text-xs">{item.color}</code>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </Celula>
                      <Celula>{item.order}</Celula>
                      <Celula>
                        {item.usage.tickets === 0 ? (
                          <span className="text-xs text-muted-foreground">nenhum</span>
                        ) : (
                          <span className="text-xs tabular-nums">
                            {item.usage.tickets} {item.usage.tickets === 1 ? 'chamado' : 'chamados'}
                          </span>
                        )}
                      </Celula>
                      <Celula>
                        <EtiquetaDeAtividade ativo={item.active} />
                      </Celula>
                      <Celula>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditando(item)}
                            disabled={atividade.mutacao.isPending}
                          >
                            Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              atividade.mutacao.mutate({
                                id: item.id,
                                active: !item.active,
                                version: item.version,
                              })
                            }
                            disabled={atividade.mutacao.isPending}
                          >
                            {item.active ? 'Desativar' : 'Ativar'}
                          </Button>
                        </div>
                      </Celula>
                    </Linha>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {editando ? (
        <DialogoDeNivel
          item={editando}
          alterarFn={alterarFn}
          aoFechar={() => setEditando(null)}
        />
      ) : null}
    </section>
  );
}

function DialogoDeNivel({
  item,
  alterarFn,
  aoFechar,
}: {
  item: AdminLevelDefinition;
  alterarFn: (id: string, corpo: AlteracaoDeDefinicao) => Promise<AdminLevelDefinition>;
  aoFechar: () => void;
}) {
  const [label, setLabel] = useState(item.label);
  const [color, setColor] = useState(item.color ?? '');
  const [order, setOrder] = useState(item.order);

  const escrita = useEscritaDeCatalogo(
    () => alterarFn(item.id, { label: label.trim(), color: color.trim() || null, order, version: item.version }),
    aoFechar,
  );

  function enviar(e: FormEvent) {
    e.preventDefault();
    escrita.mutacao.mutate();
  }

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={`Editar "${item.level}"`}
      descricao="O valor do enum não muda — é o que o motor de SLA usa. Aqui você define o que o operador lê na tela."
    >
      <form onSubmit={enviar} className="space-y-4 px-5 py-4">
        <div className="space-y-1">
          <label htmlFor="nivel-label" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Rótulo
          </label>
          <input
            id="nivel-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            minLength={1}
            maxLength={80}
            className={CLASSE_DE_CAMPO}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor="nivel-color" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Cor (hex, opcional)
            </label>
            <div className="flex items-center gap-2">
              {color ? (
                <span
                  aria-hidden
                  className="h-5 w-5 flex-none rounded-full ring-1 ring-inset ring-border"
                  style={{ backgroundColor: color }}
                />
              ) : null}
              <input
                id="nivel-color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#2BBFCF"
                pattern="^#[0-9A-Fa-f]{6}$"
                maxLength={7}
                className={`${CLASSE_DE_CAMPO} font-mono text-xs`}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label htmlFor="nivel-order" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Ordem no seletor
            </label>
            <input
              id="nivel-order"
              type="number"
              min={0}
              max={9999}
              value={order}
              onChange={(e) => setOrder(Number(e.target.value))}
              className={CLASSE_DE_CAMPO}
            />
          </div>
        </div>

        {escrita.erro ? (
          <p role="alert" className="text-sm text-destructive">
            {escrita.erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={escrita.mutacao.isPending}>
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── seção de tipo de chamado ───────────────────────────────────────────────

function SecaoDeTipo() {
  const consulta = useQuery({
    queryKey: chavesDeAdministracao.tiposDeChamado,
    queryFn: listarTiposDeChamado,
  });
  const itens = consulta.data?.items ?? [];
  const [editando, setEditando] = useState<AdminTicketTypeDefinition | null>(null);

  const atividade = useEscritaDeCatalogo(
    ({ id, active, version }: { id: string; active: boolean; version: number }) =>
      alterarTipoDeChamado(id, { active, version }),
  );

  return (
    <section className="space-y-3">
      <div className="space-y-0.5">
        <h3 className="text-base font-semibold">Tipos de chamado</h3>
        <p className="text-sm text-muted-foreground">
          O rótulo que aparece na etiqueta de tipo. O código interno (INCIDENT, REQUEST…) não muda.
        </p>
      </div>

      <Card>
        <CardBody className="p-0">
          {consulta.isError ? (
            <div className="px-5 py-6">
              <ErrorState
                error={consulta.error}
                title="Não foi possível carregar"
                onRetry={() => void consulta.refetch()}
              />
            </div>
          ) : consulta.isPending ? (
            <SkeletonRegion label="Carregando tipos de chamado" className="space-y-3 px-5 py-5">
              <SkeletonText lines={5} />
            </SkeletonRegion>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <LinhaDeCabecalho>
                  <Cabecalho>Tipo</Cabecalho>
                  <Cabecalho>Rótulo</Cabecalho>
                  <Cabecalho>Ordem</Cabecalho>
                  <Cabecalho>Em uso</Cabecalho>
                  <Cabecalho>Estado</Cabecalho>
                  <Cabecalho className="text-right">Ações</Cabecalho>
                </LinhaDeCabecalho>
                <tbody>
                  {itens.map((item) => (
                    <Linha key={item.id} inativo={!item.active}>
                      <Celula>
                        <code className="font-mono text-xs text-muted-foreground">{item.type}</code>
                      </Celula>
                      <Celula className="font-medium">{item.label}</Celula>
                      <Celula>{item.order}</Celula>
                      <Celula>
                        {item.usage.tickets === 0 ? (
                          <span className="text-xs text-muted-foreground">nenhum</span>
                        ) : (
                          <span className="text-xs tabular-nums">
                            {item.usage.tickets} {item.usage.tickets === 1 ? 'chamado' : 'chamados'}
                          </span>
                        )}
                      </Celula>
                      <Celula>
                        <EtiquetaDeAtividade ativo={item.active} />
                      </Celula>
                      <Celula>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditando(item)}
                            disabled={atividade.mutacao.isPending}
                          >
                            Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              atividade.mutacao.mutate({
                                id: item.id,
                                active: !item.active,
                                version: item.version,
                              })
                            }
                            disabled={atividade.mutacao.isPending}
                          >
                            {item.active ? 'Desativar' : 'Ativar'}
                          </Button>
                        </div>
                      </Celula>
                    </Linha>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {editando ? (
        <DialogoDeTipo item={editando} aoFechar={() => setEditando(null)} />
      ) : null}
    </section>
  );
}

function DialogoDeTipo({
  item,
  aoFechar,
}: {
  item: AdminTicketTypeDefinition;
  aoFechar: () => void;
}) {
  const [label, setLabel] = useState(item.label);
  const [order, setOrder] = useState(item.order);

  const escrita = useEscritaDeCatalogo(
    () => alterarTipoDeChamado(item.id, { label: label.trim(), order, version: item.version }),
    aoFechar,
  );

  function enviar(e: FormEvent) {
    e.preventDefault();
    escrita.mutacao.mutate();
  }

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={`Editar "${item.type}"`}
      descricao="O código interno não muda. Aqui você define o rótulo que aparece na etiqueta de tipo."
    >
      <form onSubmit={enviar} className="space-y-4 px-5 py-4">
        <div className="space-y-1">
          <label htmlFor="tipo-label" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Rótulo
          </label>
          <input
            id="tipo-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            minLength={1}
            maxLength={80}
            className={CLASSE_DE_CAMPO}
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="tipo-order" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Ordem no seletor
          </label>
          <input
            id="tipo-order"
            type="number"
            min={0}
            max={9999}
            value={order}
            onChange={(e) => setOrder(Number(e.target.value))}
            className={CLASSE_DE_CAMPO}
          />
        </div>

        {escrita.erro ? (
          <p role="alert" className="text-sm text-destructive">
            {escrita.erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={escrita.mutacao.isPending}>
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── página ─────────────────────────────────────────────────────────────────

export function TicketFieldDefinitionsPage() {
  return (
    <div className="animate-fade-in space-y-10">
      <SecaoDeNivel
        titulo="Prioridade"
        resumo="Rótulo da prioridade calculada pela matriz impacto × urgência. A cor aparece na etiqueta da lista de chamados."
        chaveDeConsulta={chavesDeAdministracao.prioridades}
        queryFn={listarPrioridades}
        alterarFn={alterarPrioridade}
      />
      <SecaoDeNivel
        titulo="Urgência"
        resumo="Um dos dois eixos da matriz. O operador escolhe na classificação do chamado."
        chaveDeConsulta={chavesDeAdministracao.urgencias}
        queryFn={listarUrgencias}
        alterarFn={alterarUrgencia}
      />
      <SecaoDeNivel
        titulo="Impacto"
        resumo="O outro eixo da matriz. Combinado com a urgência, determina a prioridade."
        chaveDeConsulta={chavesDeAdministracao.impactos}
        queryFn={listarImpactos}
        alterarFn={alterarImpacto}
      />
      <SecaoDeTipo />
    </div>
  );
}
